import React, {
  useReducer,
  useEffect,
  useContext,
  useCallback,
  useState,
} from 'react'
import moment from 'moment-timezone'
import { useJsApiLoader } from '@react-google-maps/api'
import 'moment/locale/fr'
import 'moment/locale/de'
import 'moment/locale/es'
import 'moment/locale/pt'
import { allowFitOnLanguage } from 'hooks/usePagePathType'
import useLokalise from 'lokalise/lokalise'
import { REACT_APP_GOOGLE_MAPS_API_KEY } from 'config'
import {
  clearUserInfo,
  getStateId,
  getToken,
  setHealthieProviderId,
  setStateId,
} from 'utils/authLocalStorage'
import _ from 'lodash'
import { useMetadataOptions } from 'hooks/careHooks'
import { getUserProfilePromise } from 'apis/FitOnCareAPI'
import { amplitudeLogout } from 'utils/amplitudeInit'

export const AuthContext = React.createContext({})
const libraries = ['places']
const initialState = {
  personalInfo: {},
  survey: {},
  hormonalAutoimmune: {},
  profile: {},
  pastDiagnosis: [],
  hearAbout: {},
  talkAboutCoverage: {},
  insuranceCardEntry: {},
  primaryPhysician: [],
  metadataOptions: {},
  isClearAll: false,
  rehabQuestion1: {},
  rehabQuestion2: {},
  userProfile: {},
  rehabSelectedThemes: [],
  allThemes: [],
  bookedAppointment: null,
  skipToScheduledTime: false,
  insuranceCardFiles: {},
  unavailableStatus: null,
  uhOhStatus: null,
  stateList: [],
  mcaInfo: {},
  candidateTracks: [],
}

const reducer = (state, action) => {
  switch (action.type) {
    case 'SET_PERSONAL_INFO':
      return {
        ...state,
        personalInfo: {
          ...state.personalInfo,
          ...(action.payload.personalInfo || {}),
        },
      }
    case 'SET_SURVEY':
      return {
        ...state,
        survey: action.payload.survey || {},
      }
    case 'SET_HORMONAL_AUTOIMMUNE':
      return {
        ...state,
        hormonalAutoimmune: action.payload.hormonalAutoimmune || {},
      }
    case 'SET_PROFILE':
      return {
        ...state,
        profile: action.payload.profile || {},
      }
    case 'SET_PAST_DIAGNOSIS':
      return {
        ...state,
        pastDiagnosis: action.payload.pastDiagnosis || [],
      }
    case 'SET_HEAR_ABOUT':
      return {
        ...state,
        hearAbout: action.payload.hearAbout || {},
      }
    case 'SET_TALK_ABOUT_COVERAGE':
      return {
        ...state,
        talkAboutCoverage: action.payload.talkAboutCoverage || {},
      }
    case 'SET_INSURANCE_CARD_ENTRY':
      return {
        ...state,
        insuranceCardEntry: action.payload.insuranceCardEntry || {},
      }
    case 'PRIMARY_PHYSICIAN':
      return {
        ...state,
        primaryPhysician: action.payload.primaryPhysician || [],
      }
    case 'METADATA_OPTIONS':
      return {
        ...state,
        metadataOptions: action.payload.metadataOptions || {},
      }
    case 'SET_CLEAR_DATA':
      return {
        ...state,
        isClearAll: action.payload.isClearAll || false,
      }
    case 'SET_REHAB_QUESTION_1':
      return {
        ...state,
        rehabQuestion1: action.payload.rehabQuestion1 || {},
      }
    case 'SET_REHAB_QUESTION_2':
      return {
        ...state,
        rehabQuestion2: action.payload.rehabQuestion2 || {},
      }
    case 'SET_USER_PROFILE':
      return {
        ...state,
        userProfile: action.payload.userProfile || {},
      }
    case 'SET_REHAB_SELECTED_THEMES':
      return {
        ...state,
        rehabSelectedThemes: action.payload.rehabSelectedThemes || [],
      }
    case 'SET_ALL_THEMES':
      return {
        ...state,
        allThemes: action.payload.allThemes || [],
      }
    case 'SET_BOOKED_APPOINTMENT':
      return {
        ...state,
        bookedAppointment: action.payload.bookedAppointment || null,
      }
    case 'SET_SKIP_TO_SCHEDULED_TIME':
      return {
        ...state,
        skipToScheduledTime: action.payload.skipToScheduledTime || false,
      }
    case 'CLEAR_DATA':
      return {
        ...initialState,
        isClearAll: true,
        stateList: [...(state?.stateList || [])],
      }
    case 'SET_INSURANCE_CARD_FILES':
      return {
        ...state,
        insuranceCardFiles: action.payload.insuranceCardFiles || [],
      }
    case 'SET_UNAVAILABLE_STATUS':
      return {
        ...state,
        unavailableStatus: action.payload.unavailableStatus || null,
      }
    case 'SET_STATE_LIST':
      return {
        ...state,
        stateList: action.payload.stateList || [],
      }
    case 'SET_UH_OH_STATUS':
      return {
        ...state,
        uhOhStatus: action.payload.uhOhStatus || null,
      }
    case 'SET_MCA_INFO':
      return {
        ...state,
        mcaInfo: action.payload.mcaInfo || {},
      }
    case 'SET_CANDIDATE_TRACKS':
      return {
        ...state,
        candidateTracks: action.payload.candidateTracks || [],
      }
    default:
      throw new Error()
  }
}

export const AuthContextProvider = (Children) => {
  const Provider = (props) => {
    const [state, dispatch] = useReducer(reducer, initialState)
    const { lokalise, localCode, localLang, langLoading, dateFnsLocale } =
      useLokalise()

    useEffect(() => {
      if (localCode === 'en' || !allowFitOnLanguage()) {
        moment.locale('en')

        moment.updateLocale('en', {
          weekdaysMin: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
          week: {
            dow: 1, // Monday is the first day of the week.
          },
        })
      } else if (localCode) {
        moment.locale(localLang)

        moment.updateLocale(localCode)
      }
      // setLocalLanguage(localLang)
    }, [localCode, localLang])

    const clearData = () => {
      dispatch({
        type: 'CLEAR_DATA',
        payload: {},
      })
    }

    const { isLoaded: isGoogleMapLoaded } = useJsApiLoader({
      id: 'google-map-script',
      googleMapsApiKey: REACT_APP_GOOGLE_MAPS_API_KEY,
      libraries,
    })

    const updateUserProfile = useCallback((userProfile) => {
      dispatch({
        type: 'SET_USER_PROFILE',
        payload: {
          userProfile: userProfile || {},
        },
      })
    }, [])

    return (
      <AuthContext.Provider
        value={{
          ...state,
          dispatch,
          lokalise,
          localCode,
          localLang,
          langLoading,
          dateFnsLocale,
          isGoogleMapLoaded,
          clearData,
          updateUserProfile,
        }}
      >
        <Children {...props} />
      </AuthContext.Provider>
    )
  }
  return Provider
}

export const useI18n = () => {
  const {
    lokalise: i18n,
    localCode,
    localLang,
    langLoading,
    dateFnsLocale,
  } = useContext(AuthContext)
  return { i18n, localCode, localLang, langLoading, dateFnsLocale }
}

export const usePersonalInfo = () => {
  const { personalInfo, dispatch } = useContext(AuthContext)
  const setPersonalInfo = ({
    firstName,
    firstNameForMca,
    lastName,
    phone,
    email,
    state,
  }) => {
    dispatch({
      type: 'SET_PERSONAL_INFO',
      payload: {
        personalInfo: {
          firstName,
          firstNameForMca,
          lastName,
          phone,
          email,
          state,
        },
      },
    })
  }
  return { personalInfo, setPersonalInfo }
}

export const useSurvey = () => {
  const { survey, dispatch } = useContext(AuthContext)
  const setSurvey = (survey) => {
    dispatch({
      type: 'SET_SURVEY',
      payload: {
        survey,
      },
    })
  }
  return { survey, setSurvey }
}

export const useHormonalAutoimmune = () => {
  const { hormonalAutoimmune, dispatch } = useContext(AuthContext)
  const setHormonalAutoimmune = (hormonalAutoimmune) => {
    dispatch({
      type: 'SET_HORMONAL_AUTOIMMUNE',
      payload: {
        hormonalAutoimmune,
      },
    })
  }
  return { hormonalAutoimmune, setHormonalAutoimmune }
}

export const useProfile = () => {
  const { profile, dispatch } = useContext(AuthContext)
  const setProfile = ({ birthday, gender, heightFt, heightIn, weightLbs }) => {
    dispatch({
      type: 'SET_PROFILE',
      payload: {
        profile: { birthday, gender, heightFt, heightIn, weightLbs },
      },
    })
  }
  return { profile, setProfile }
}

export const usePastDiagnosis = () => {
  const { pastDiagnosis, dispatch } = useContext(AuthContext)
  const setPastDiagnosis = (pastDiagnosis) => {
    dispatch({
      type: 'SET_PAST_DIAGNOSIS',
      payload: {
        pastDiagnosis,
      },
    })
  }
  return { pastDiagnosis, setPastDiagnosis }
}

export const useRehabQuestion1 = () => {
  const { rehabQuestion1, dispatch } = useContext(AuthContext)
  const setRehabQuestion1 = (rehabQuestion1) => {
    dispatch({
      type: 'SET_REHAB_QUESTION_1',
      payload: {
        rehabQuestion1,
      },
    })
  }
  return { rehabQuestion1, setRehabQuestion1 }
}

export const useRehabQuestion2 = () => {
  const { rehabQuestion2, dispatch } = useContext(AuthContext)
  const setRehabQuestion2 = (rehabQuestion2) => {
    dispatch({
      type: 'SET_REHAB_QUESTION_2',
      payload: {
        rehabQuestion2,
      },
    })
  }
  return { rehabQuestion2, setRehabQuestion2 }
}

export const useRehabSelectedThemes = () => {
  const { rehabSelectedThemes, dispatch } = useContext(AuthContext)
  const setRehabSelectedThemes = (rehabSelectedThemes) => {
    dispatch({
      type: 'SET_REHAB_SELECTED_THEMES',
      payload: {
        rehabSelectedThemes,
      },
    })
  }
  return { rehabSelectedThemes, setRehabSelectedThemes }
}

export const useAllThemes = () => {
  const { allThemes, dispatch } = useContext(AuthContext)
  const setAllThemes = (allThemes) => {
    dispatch({
      type: 'SET_ALL_THEMES',
      payload: { allThemes },
    })
  }
  return { allThemes, setAllThemes }
}

export const useHearAbout = () => {
  const { hearAbout, dispatch } = useContext(AuthContext)
  const setHearAbout = (hearAbout) => {
    dispatch({
      type: 'SET_HEAR_ABOUT',
      payload: {
        hearAbout,
      },
    })
  }
  return { hearAbout, setHearAbout }
}

export const useTalkAboutCoverage = () => {
  const { talkAboutCoverage, dispatch } = useContext(AuthContext)
  const setTalkAboutCoverage = (talkAboutCoverage) => {
    dispatch({
      type: 'SET_TALK_ABOUT_COVERAGE',
      payload: {
        talkAboutCoverage,
      },
    })
  }
  return { talkAboutCoverage, setTalkAboutCoverage }
}

export const useInsuranceCardEntry = () => {
  const { insuranceCardEntry, dispatch } = useContext(AuthContext)
  const setInsuranceCardEntry = ({
    insurancePlan,
    coverage,
    type,
    relationship,
    policyNumber,
    groupNumber,
    companyPhone,
    addressInfo,
    policyHolderInfo,
  }) => {
    dispatch({
      type: 'SET_INSURANCE_CARD_ENTRY',
      payload: {
        insuranceCardEntry: {
          insurancePlan,
          coverage,
          type,
          relationship,
          policyNumber,
          groupNumber,
          companyPhone,
          addressInfo,
          policyHolderInfo,
        },
      },
    })
  }
  return { insuranceCardEntry, setInsuranceCardEntry }
}

export const useLoadGoogleMap = () => {
  const { isGoogleMapLoaded } = useContext(AuthContext)
  return { isGoogleMapLoaded }
}

export const useStateList = () => {
  const { stateList, dispatch } = useContext(AuthContext)
  const setStateList = (stateList) => {
    dispatch({
      type: 'SET_STATE_LIST',
      payload: { stateList },
    })
  }
  const getStateById = (stateId) => {
    const _state = _.find(stateList, { id: +stateId })
    return _state
  }
  return { stateList, setStateList, getStateById }
}

export const useHealthieInfo = ({ user = {}, supportForNewWelcome }) => {
  const { setPersonalInfo } = usePersonalInfo()
  const { setSurvey } = useSurvey()
  const { setHormonalAutoimmune } = useHormonalAutoimmune()
  const { setProfile } = useProfile()
  const { setHearAbout } = useHearAbout()
  const { talkAboutCoverage, setTalkAboutCoverage } = useTalkAboutCoverage()
  const { setInsuranceCardEntry } = useInsuranceCardEntry()
  const { setPastDiagnosis } = usePastDiagnosis()
  const {
    helpMostOptions,
    diseaseHelpOptions,
    hearAboutOptions,
    insuranceProviders,
    pastDiagnosisOptions,
  } = useMetadataOptions()
  const { stateList } = useStateList()
  const { userSelectOptions, skuCondition } = useUserProfile()

  const updatePastDiagnosis = useCallback(() => {
    if (_.isEmpty(pastDiagnosisOptions)) return

    const {
      wlc_primary: wlcPrimary = [],
      wlc_primary_other: wlcPrimaryOther = [],
    } = skuCondition

    const pastDiagnosis = pastDiagnosisOptions
      .filter(
        (item) =>
          wlcPrimary.includes(item.id) ||
          wlcPrimary.includes(item.id?.toString())
      )
      .map((item) => {
        if (wlcPrimaryOther.length > 0 && item.name.toLowerCase() === 'other') {
          return { ...item, otherText: wlcPrimaryOther[0] }
        }
        return item
      })

    setPastDiagnosis(pastDiagnosis)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pastDiagnosisOptions, skuCondition])

  const updatePersonalInfo = useCallback(
    async () => {
      if (!_.isEmpty(user) && !_.isEmpty(stateList)) {
        const localStateId = getStateId()
        const _state = _.find(
          stateList,
          localStateId
            ? { id: +localStateId }
            : { legalEntityId: user.dietitian_id }
        )
        setPersonalInfo({
          firstName: user.legal_name ? user.legal_name : user.first_name,
          firstNameForMca: user.first_name,
          lastName: user.last_name,
          phone: user.phone_number,
          email: user.email,
          state: _state,
        })
        setHealthieProviderId(_state?.legalEntityId)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, stateList]
  )

  const updateSurvey = useCallback(
    () => {
      if (!_.isEmpty(helpMostOptions) && !_.isEmpty(userSelectOptions)) {
        const helpMostOptionId = +userSelectOptions.helpMostOptionId
        const helpMostOptionText = userSelectOptions.helpMostOptionText
        const selectedOption = _.find(
          helpMostOptions,
          (option) => option.id === helpMostOptionId
        )
        if (selectedOption) {
          if (selectedOption.name === 'Other') {
            setSurvey({
              ...selectedOption,
              otherText: helpMostOptionText,
            })
          } else {
            setSurvey(selectedOption)
          }
        } else {
          setSurvey({})
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [helpMostOptions, userSelectOptions]
  )

  const updateHormonalAutoimmune = useCallback(
    () => {
      if (!_.isEmpty(diseaseHelpOptions) && !_.isEmpty(userSelectOptions)) {
        const diseaseHelpOptionId = +userSelectOptions.diseaseHelpOptionId
        const diseaseHelpOptionText = userSelectOptions.diseaseHelpOptionText
        const selectedOption = _.find(
          diseaseHelpOptions,
          (option) => option.id === diseaseHelpOptionId
        )
        if (selectedOption) {
          if (selectedOption.name === 'Other') {
            setHormonalAutoimmune({
              ...selectedOption,
              otherText: diseaseHelpOptionText,
            })
          } else {
            setHormonalAutoimmune(selectedOption)
          }
        } else {
          setHormonalAutoimmune({})
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [diseaseHelpOptions, userSelectOptions]
  )

  const updateProfile = useCallback(
    () => {
      if (!_.isEmpty(user)) {
        const weightLbs = user.weight
          ? parseInt(user.weight.replace(' Lbs', ''))
          : ''
        setProfile({
          birthday: user.dob
            ? moment(user.dob, 'YYYY-MM-DD').format('MM/DD/YYYY')
            : '',
          gender: user.gender || '',
          heightFt: user.height ? Math.floor(+user.height / 12) : '',
          heightIn: user.height ? +user.height % 12 : '',
          weightLbs: weightLbs,
        })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user.dob, user.gender, user.height, user.weight]
  )

  const updateHearAbout = useCallback(
    () => {
      if (!_.isEmpty(user) && hearAboutOptions) {
        const refType = user.client_source?.ref_type
        const refSource = user.client_source?.related_object?.full_name
        let hearItem = {}
        if (refType === 'ad') {
          if (refSource === 'ZocDoc') {
            hearItem = _.find(
              hearAboutOptions,
              (option) => option.name === 'ZocDoc'
            )
          } else if (refSource === 'Other') {
            hearItem = _.find(
              hearAboutOptions,
              (option) => option.name === 'Advertisement'
            )
          }
        } else if (refType === 'other') {
          hearItem = _.find(
            hearAboutOptions,
            (option) => option.name === refSource
          )
          if (_.isEmpty(hearItem)) {
            hearItem = _.find(
              hearAboutOptions,
              (option) => option.name === 'Other'
            )
            hearItem = { ...hearItem, otherText: refSource }
          }
        }
        setHearAbout(hearItem)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, hearAboutOptions]
  )

  const updateTalkAbout = useCallback(
    () => {
      // add condition to check if talkAboutCoverage is empty, prescreen will set the talkAboutCoverage, so we don't need to update it again
      if (
        !_.isEmpty(insuranceProviders) &&
        !_.isEmpty(userSelectOptions) &&
        _.isEmpty(supportForNewWelcome)
      ) {
        const insuranceProviderOptionId =
          +userSelectOptions.insuranceProviderOptionId
        const insuranceProviderOptionText =
          userSelectOptions.insuranceProviderOptionText
        const selectedProvider = _.find(
          insuranceProviders,
          (provider) => provider.id === insuranceProviderOptionId
        )
        if (selectedProvider) {
          if (selectedProvider.name === 'Other') {
            setTalkAboutCoverage({
              ...selectedProvider,
              otherText: insuranceProviderOptionText,
            })
          } else {
            setTalkAboutCoverage(selectedProvider)
          }
        } else {
          setTalkAboutCoverage({})
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [insuranceProviders, userSelectOptions, supportForNewWelcome]
  )

  const updateInsuranceCardEntry = useCallback(
    () => {
      if (!_.isEmpty(user)) {
        const policy = !_.isEmpty(user.policies)
          ? user.policies[user.policies.length - 1]
          : {}
        const location = !_.isEmpty(user.locations)
          ? user.locations[user.locations.length - 1]
          : {}
        const coverage =
          _.filter(user.active_tags || [], (tag) =>
            ['HMO', 'PPO'].includes(tag.name)
          )[0]?.name || null
        const insurancePlan = !_.isEmpty(policy) ? policy.insurance_plan : {}
        const type = !_.isEmpty(policy) ? policy.priority_type : null
        const relationship = !_.isEmpty(policy)
          ? policy.holder_relationship
          : null
        const policyNumber = !_.isEmpty(policy) ? policy.num : null
        const groupNumber = !_.isEmpty(policy) ? policy.group_num : null
        const companyPhone = !_.isEmpty(policy)
          ? policy.policy_phone_number
          : null
        const addressInfo = !_.isEmpty(location)
          ? {
              line1: location.line1,
              line2: location.line2,
              city: location.city,
              state: _.find(
                stateList || [],
                (option) => option.code === location.state
              ),
              postcode: location.zip,
            }
          : {}
        const policyHolderInfo = !_.isEmpty(policy)
          ? {
              firstName: policy.holder_first,
              gender: policy.holder_gender,
              lastName: policy.holder_last,
              dob: policy.holder_dob
                ? moment(policy.holder_dob, 'YYYY-MM-DD').format('MM/DD/YYYY')
                : '',
            }
          : {}
        setInsuranceCardEntry({
          insurancePlan,
          coverage,
          type,
          relationship,
          policyNumber,
          groupNumber,
          companyPhone,
          addressInfo,
          policyHolderInfo,
        })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, stateList]
  )

  useEffect(() => {
    updatePastDiagnosis()
  }, [updatePastDiagnosis])

  useEffect(() => {
    updatePersonalInfo()
  }, [updatePersonalInfo])

  useEffect(() => {
    updateSurvey()
  }, [updateSurvey])

  useEffect(() => {
    updateHormonalAutoimmune()
  }, [updateHormonalAutoimmune])

  useEffect(() => {
    updateProfile()
  }, [updateProfile])

  useEffect(() => {
    updateHearAbout()
  }, [updateHearAbout])

  useEffect(() => {
    updateTalkAbout()
  }, [updateTalkAbout])

  useEffect(() => {
    updateInsuranceCardEntry()
  }, [updateInsuranceCardEntry])
}

export const useUserProfile = () => {
  const [userSelectOptions, setUserSelectOptions] = useState({})
  const [skuCondition, setSkuCondition] = useState({})
  const { userProfile, updateUserProfile } = useContext(AuthContext)

  const getUserProfile = useCallback(async () => {
    const token = getToken()
    if (token) {
      const resp = await getUserProfilePromise()
      if (!_.isEmpty(resp.data)) {
        setUserSelectOptions(resp.data.userSelectOptions || {})
        setStateId(resp.data.localeStateId)
        setSkuCondition(resp.data.skuCondition || {})
        updateUserProfile(resp.data)
        return resp.data
      }
    }
    return {}
  }, [updateUserProfile])

  useEffect(() => {
    setUserSelectOptions(userProfile.userSelectOptions || {})
    setStateId(userProfile.localeStateId || '')
    setSkuCondition(userProfile.skuCondition || {})
  }, [userProfile])

  return {
    userSelectOptions,
    getUserProfile,
    skuCondition,
    userProfile,
  }
}

export const useClearUserInfo = () => {
  const { isClearAll, clearData, dispatch } = useContext(AuthContext)
  const setIsClearAll = (value) => {
    dispatch({
      type: 'SET_CLEAR_DATA',
      payload: {
        isClearAll: value,
      },
    })
  }

  const clearAll = () => {
    clearUserInfo()
    clearData()
    amplitudeLogout()
  }
  return { clearAll, isClearAll, setIsClearAll }
}

export const useBookedAppointment = () => {
  const { bookedAppointment, dispatch } = useContext(AuthContext)
  const setBookedAppointment = (bookedAppointment) => {
    dispatch({
      type: 'SET_BOOKED_APPOINTMENT',
      payload: { bookedAppointment },
    })
  }
  return { bookedAppointment, setBookedAppointment }
}

export const useSkipToScheduledTime = () => {
  const { skipToScheduledTime, dispatch } = useContext(AuthContext)
  const setSkipToScheduledTime = (skipToScheduledTime) => {
    dispatch({
      type: 'SET_SKIP_TO_SCHEDULED_TIME',
      payload: { skipToScheduledTime },
    })
  }
  return { skipToScheduledTime, setSkipToScheduledTime }
}

export const useInsuranceCardFiles = () => {
  const { insuranceCardFiles, dispatch } = useContext(AuthContext)
  const setInsuranceCardFiles = (insuranceCardFiles) => {
    dispatch({
      type: 'SET_INSURANCE_CARD_FILES',
      payload: { insuranceCardFiles },
    })
  }
  return { insuranceCardFiles, setInsuranceCardFiles }
}

export const useUnavailableStatus = () => {
  const { unavailableStatus, dispatch } = useContext(AuthContext)
  const setUnavailableStatus = (unavailableStatus) => {
    dispatch({
      type: 'SET_UNAVAILABLE_STATUS',
      payload: { unavailableStatus },
    })
  }
  return { unavailableStatus, setUnavailableStatus }
}

export const useUhOhStatus = () => {
  const { uhOhStatus, dispatch } = useContext(AuthContext)
  const setUhOhStatus = (uhOhStatus) => {
    dispatch({
      type: 'SET_UH_OH_STATUS',
      payload: { uhOhStatus },
    })
  }
  return { uhOhStatus, setUhOhStatus }
}

export const useMCAInfo = () => {
  const { mcaInfo, dispatch } = useContext(AuthContext)
  const setMCAInfo = (mcaInfo) => {
    if (window.isDev) console.log('setMCAInfo', mcaInfo)
    dispatch({
      type: 'SET_MCA_INFO',
      payload: { mcaInfo },
    })
  }
  return { mcaInfo, setMCAInfo }
}

export const useCandidateTracks = () => {
  const { candidateTracks, dispatch } = useContext(AuthContext)
  const setCandidateTracks = (candidateTracks) => {
    dispatch({
      type: 'SET_CANDIDATE_TRACKS',
      payload: { candidateTracks },
    })
  }
  return { candidateTracks, setCandidateTracks }
}
